import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useLang, track } from "@/context/LanguageContext";
import { SectionLabel } from "@/components/Editorial";
import { PageHead } from "@/components/Shared";

import { API } from "@/lib/api";

const FOCUS_KEYS = ["strength", "core", "posture", "mobility", "technique", "fullbody", "attention", "other"];

const COPY = {
  en: {
    label: "Private Pilates",
    title: "Before we begin.",
    lead: "To help us prepare for your Private Pilates session, please complete this short intake before your appointment. It only takes a few minutes and allows your instructor to understand your experience, goals and any relevant physical considerations before you arrive.",
    reassurance: "Your information is treated with care and only used to prepare and deliver your Private Pilates experience.",
    stepOf: (n) => `Step ${n} of 4`,
    steps: ["About you", "Your practice", "Good to know", "Confirmation"],
    back: "Back",
    next: "Continue",
    // step 1
    fullName: "Full name",
    dob: "Date of birth",
    email: "Email address",
    phone: "Phone number",
    // step 2
    experienceQ: "What experience do you have with Pilates?",
    experienceOptions: [["first_time", "This is my first time"], ["mat", "Mat Pilates"], ["reformer", "Reformer Pilates"], ["both", "Both Mat & Reformer Pilates"]],
    focusQ: "What would you like to focus on during your private session?",
    focusHint: "Select all that apply.",
    focusOptions: { strength: "Strength", core: "Core stability", posture: "Posture", mobility: "Mobility & flexibility", technique: "Technique", fullbody: "Full-body training", attention: "Personal attention / corrections", other: "Other" },
    focusOtherLabel: "Tell us briefly what you have in mind (optional)",
    goalsQ: "Is there anything specific you would like your instructor to know about your goals for this session?",
    optional: "Optional",
    // step 3
    complaintsQ: "Do you currently have any pain, injuries or physical complaints that your instructor should know about?",
    avoidQ: "Are there any movements you currently avoid or have been advised not to perform?",
    treatmentQ: "Have you recently had surgery or are you currently receiving relevant treatment from a doctor, physiotherapist or other healthcare professional?",
    pregnancyQ: "Are you currently pregnant or have you recently given birth?",
    otherQ: "Is there anything else your instructor should know before your private session?",
    tellMore: "Please tell us a little more.",
    shareRelevant: "Please share any information relevant to your session.",
    pregnancyDetail: "Is there anything relevant you would like your instructor to know? (optional)",
    no: "No",
    yes: "Yes",
    preferNot: "Prefer not to say",
    // step 4
    importantLabel: "Important",
    importantBody: "Private Reformer Pilates is not medical treatment. If you are unsure whether exercise is appropriate for you, please consult an appropriate medical or healthcare professional before participating. During your session, always tell your instructor immediately if an exercise causes pain, dizziness or unusual discomfort.",
    consents: [
      "I confirm that the information I have provided is accurate and complete to the best of my knowledge.",
      "I understand that I should inform my instructor of relevant changes to my physical condition before or during my session.",
      "I understand that I should immediately inform my instructor if I experience pain, dizziness or unusual discomfort during training.",
      "I consent to Maison Core processing the relevant information I provide in this intake for the purpose of preparing and delivering my Private Pilates session.",
    ],
    termsPre: "I have read and accept the Maison Core ",
    termsLink: "Terms & Conditions",
    termsPost: ".",
    signatureLabel: "Full name as digital confirmation",
    dateLabel: "Date",
    submit: "Submit my intake",
    sending: "Sending…",
    // validation
    vStep1: "Please complete your name, date of birth, email and phone number.",
    vEmail: "Please enter a valid email address.",
    vExperience: "Please select your Pilates experience.",
    vComplaints: "Please tell us a little more about your pain, injuries or complaints.",
    vAvoid: "Please tell us a little more about the movements you avoid.",
    vConsents: "Please confirm all statements before submitting.",
    vSignature: "Please type your full name as digital confirmation.",
    error: "Something went wrong. Please try again.",
    // success
    successTitle: "You're all set.",
    successBody: "Thank you. Your intake has been received. Your instructor can now prepare for your Private Pilates session. We look forward to welcoming you to Maison Core.",
    beforeLabel: "Before your session",
    before: ["Please arrive a few minutes early", "Grip socks are required", "Wear comfortable sportswear", "Bring water if desired"],
    backHome: "Back to Maison Core",
  },
  nl: {
    label: "Private Pilates",
    title: "Voordat we beginnen.",
    lead: "Om jouw Private Pilates sessie goed voor te bereiden, vragen we je vooraf een korte intake in te vullen. Het kost slechts een paar minuten en helpt je instructor om rekening te houden met jouw ervaring, doelen en relevante aandachtspunten.",
    reassurance: "Je gegevens worden zorgvuldig behandeld en uitsluitend gebruikt om jouw Private Pilates ervaring voor te bereiden en te verzorgen.",
    stepOf: (n) => `Stap ${n} van 4`,
    steps: ["Over jou", "Jouw practice", "Goed om te weten", "Bevestiging"],
    back: "Terug",
    next: "Verder",
    fullName: "Volledige naam",
    dob: "Geboortedatum",
    email: "E-mailadres",
    phone: "Telefoonnummer",
    experienceQ: "Welke ervaring heb je met Pilates?",
    experienceOptions: [["first_time", "Dit is mijn eerste keer"], ["mat", "Mat Pilates"], ["reformer", "Reformer Pilates"], ["both", "Zowel Mat als Reformer Pilates"]],
    focusQ: "Waar wil je je op richten tijdens je privésessie?",
    focusHint: "Selecteer alles wat van toepassing is.",
    focusOptions: { strength: "Kracht", core: "Core-stabiliteit", posture: "Houding", mobility: "Mobiliteit & flexibiliteit", technique: "Techniek", fullbody: "Full-body training", attention: "Persoonlijke aandacht / correcties", other: "Anders" },
    focusOtherLabel: "Vertel ons kort waar je aan denkt (optioneel)",
    goalsQ: "Is er iets specifieks dat je instructor moet weten over jouw doelen voor deze sessie?",
    optional: "Optioneel",
    complaintsQ: "Heb je op dit moment pijn, blessures of fysieke klachten waar je instructor rekening mee moet houden?",
    avoidQ: "Zijn er bewegingen die je momenteel vermijdt of die je zijn afgeraden?",
    treatmentQ: "Ben je recent geopereerd of ben je momenteel onder behandeling bij een arts, fysiotherapeut of andere zorgverlener?",
    pregnancyQ: "Ben je momenteel zwanger of ben je recent bevallen?",
    otherQ: "Is er verder nog iets dat je instructor moet weten vóór je privésessie?",
    tellMore: "Vertel ons iets meer.",
    shareRelevant: "Deel gerust wat relevant is voor je sessie.",
    pregnancyDetail: "Is er iets relevants dat je instructor moet weten? (optioneel)",
    no: "Nee",
    yes: "Ja",
    preferNot: "Zeg ik liever niet",
    importantLabel: "Belangrijk",
    importantBody: "Private Reformer Pilates is geen medische behandeling. Twijfel je of bewegen op dit moment geschikt voor je is, raadpleeg dan eerst een arts of andere zorgverlener. Geef tijdens je sessie altijd direct aan je instructor door als een oefening pijn, duizeligheid of ongewoon ongemak veroorzaakt.",
    consents: [
      "Ik bevestig dat de informatie die ik heb verstrekt naar mijn beste weten juist en volledig is.",
      "Ik begrijp dat ik relevante veranderingen in mijn fysieke gesteldheid vóór of tijdens mijn sessie aan mijn instructor doorgeef.",
      "Ik begrijp dat ik het direct aan mijn instructor meld als ik tijdens de training pijn, duizeligheid of ongewoon ongemak ervaar.",
      "Ik geef Maison Core toestemming om de relevante informatie uit deze intake te verwerken voor het voorbereiden en verzorgen van mijn Private Pilates sessie.",
    ],
    termsPre: "Ik heb de ",
    termsLink: "Algemene Voorwaarden",
    termsPost: " van Maison Core gelezen en accepteer deze.",
    signatureLabel: "Volledige naam als digitale bevestiging",
    dateLabel: "Datum",
    submit: "Verstuur mijn intake",
    sending: "Versturen…",
    vStep1: "Vul je naam, geboortedatum, e-mailadres en telefoonnummer in.",
    vEmail: "Vul een geldig e-mailadres in.",
    vExperience: "Selecteer je Pilates-ervaring.",
    vComplaints: "Vertel ons iets meer over je pijn, blessures of klachten.",
    vAvoid: "Vertel ons iets meer over de bewegingen die je vermijdt.",
    vConsents: "Bevestig alle verklaringen voordat je verstuurt.",
    vSignature: "Typ je volledige naam als digitale bevestiging.",
    error: "Er ging iets mis. Probeer het opnieuw.",
    successTitle: "Je bent er klaar voor.",
    successBody: "Dank je wel. Je intake is ontvangen. Je instructor kan zich nu voorbereiden op jouw Private Pilates sessie. We verwelkomen je graag bij Maison Core.",
    beforeLabel: "Voor je sessie",
    before: ["Kom een paar minuten van tevoren", "Gripsokken zijn verplicht", "Draag comfortabele sportkleding", "Neem eventueel water mee"],
    backHome: "Terug naar Maison Core",
  },
};

const field = "w-full border-b border-onyx/25 bg-transparent py-4 font-sans text-lg text-onyx placeholder:text-onyx/40 focus:border-onyx focus:outline-none transition-colors";
const labelCls = "label text-terracotta";

const YesNo = ({ value, onChange, options, testid }) => (
  <div className="mt-4 inline-flex flex-wrap border border-onyx/25 p-1" data-testid={testid}>
    {options.map(([v, l]) => (
      <button
        type="button"
        key={v}
        onClick={() => onChange(v)}
        className={`label px-6 py-3 transition-colors duration-300 md:px-8 ${value === v ? "bg-onyx text-petal" : "text-onyx/55 hover:text-onyx"}`}
        data-testid={`${testid}-${v}`}
        aria-pressed={value === v}
      >
        {l}
      </button>
    ))}
  </div>
);

const initialForm = {
  full_name: "", date_of_birth: "", email: "", phone: "",
  experience: "", focus_areas: [], focus_other: "", goals_notes: "",
  complaints: "no", complaints_detail: "",
  avoids: "no", avoid_detail: "",
  treatment: "no", treatment_detail: "",
  pregnancy: "no", pregnancy_detail: "",
  other_notes: "",
  c1: false, c2: false, c3: false, c4: false, c5: false,
  digital_signature: "",
  website: "",
};

export default function PrivateIntake() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState("idle");
  const [submitted, setSubmitted] = useState(false);
  const [f, setF] = useState(initialForm);

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  const today = useMemo(() => new Date().toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "long", year: "numeric" }), [lang]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setV = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggleFocus = (key) => setF((p) => ({ ...p, focus_areas: p.focus_areas.includes(key) ? p.focus_areas.filter((x) => x !== key) : [...p.focus_areas, key] }));

  const goTo = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const validateStep = (n) => {
    if (n === 1) {
      if (!f.full_name.trim() || !f.date_of_birth || !f.email.trim() || !f.phone.trim()) return c.vStep1;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return c.vEmail;
    }
    if (n === 2 && !f.experience) return c.vExperience;
    if (n === 3) {
      if (f.complaints === "yes" && !f.complaints_detail.trim()) return c.vComplaints;
      if (f.avoids === "yes" && !f.avoid_detail.trim()) return c.vAvoid;
    }
    if (n === 4) {
      if (!(f.c1 && f.c2 && f.c3 && f.c4 && f.c5)) return c.vConsents;
      if (!f.digital_signature.trim()) return c.vSignature;
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    track("private_intake_step", { step: step + 1 });
    goTo(step + 1);
  };

  const submit = async () => {
    const err = validateStep(4);
    if (err) { toast.error(err); return; }
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/private-intake`, {
        full_name: f.full_name.trim(),
        date_of_birth: f.date_of_birth,
        email: f.email.trim(),
        phone: f.phone.trim(),
        experience: f.experience,
        focus_areas: f.focus_areas,
        focus_other: f.focus_areas.includes("other") ? f.focus_other.trim() || null : null,
        goals_notes: f.goals_notes.trim() || null,
        has_complaints: f.complaints === "yes",
        complaints_detail: f.complaints === "yes" ? f.complaints_detail.trim() : null,
        avoids_movements: f.avoids === "yes",
        avoid_detail: f.avoids === "yes" ? f.avoid_detail.trim() : null,
        has_treatment: f.treatment === "yes",
        treatment_detail: f.treatment === "yes" ? f.treatment_detail.trim() || null : null,
        pregnancy: f.pregnancy,
        pregnancy_detail: f.pregnancy === "yes" ? f.pregnancy_detail.trim() || null : null,
        other_notes: f.other_notes.trim() || null,
        consent_accurate: f.c1,
        consent_inform_changes: f.c2,
        consent_inform_discomfort: f.c3,
        consent_processing: f.c4,
        consent_terms: f.c5,
        digital_signature: f.digital_signature.trim(),
        language: lang,
        website: f.website,
      });
      track("private_intake_submit");
      setStatus("success");
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch {
      setStatus("idle");
      toast.error(c.error);
    }
  };

  if (submitted) {
    return (
      <>
        <PageHead label={c.label} title={c.successTitle} lead={c.successBody} />
        <section className="bg-petal" data-testid="intake-success">
          <div className="mx-auto max-w-[1600px] px-6 py-16 md:px-12 md:py-24">
            <div className="max-w-2xl border-t border-onyx/15 pt-10">
              <SectionLabel>{c.beforeLabel}</SectionLabel>
              <ul className="mt-8 space-y-4">
                {c.before.map((b, i) => (
                  <li key={i} className="flex items-baseline gap-4 border-b border-onyx/10 pb-4 font-sans text-lg text-onyx/80">
                    <span className="label text-terracotta">{String(i + 1).padStart(2, "0")}</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/" className="label mt-12 inline-block border border-onyx bg-onyx px-10 py-5 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx" data-testid="intake-back-home">
                {c.backHome}
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors />
      <PageHead label={c.label} title={c.title} lead={c.lead} />

      <section className="bg-petal" data-testid="intake-body">
        <div className="mx-auto max-w-3xl px-6 pb-20 pt-4 md:px-12 md:pb-28">
          <p className="label text-onyx/45" data-testid="intake-reassurance">{c.reassurance}</p>

          {/* PROGRESS */}
          <div className="mt-12 border-t border-onyx/15 pt-8">
            <div className="flex items-baseline justify-between">
              <p className="label text-terracotta" data-testid="intake-progress">{c.stepOf(step)}</p>
              <p className="label text-onyx/50">{String(step).padStart(2, "0")} — {c.steps[step - 1]}</p>
            </div>
            <div className="mt-5 h-px w-full bg-onyx/10">
              <div className="h-px bg-onyx transition-all duration-700" style={{ width: `${(step / 4) * 100}%` }} />
            </div>
          </div>

          <div className="mt-12" data-testid={`intake-step-${step}`}>
            {/* honeypot */}
            <input type="text" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            {/* STEP 1 — ABOUT YOU */}
            {step === 1 && (
              <div className="grid gap-10 md:grid-cols-2">
                <div>
                  <label className={labelCls}>{c.fullName} *</label>
                  <input value={f.full_name} onChange={set("full_name")} autoComplete="name" className={field} data-testid="intake-fullname" />
                </div>
                <div>
                  <label className={labelCls}>{c.dob} *</label>
                  <input type="date" value={f.date_of_birth} onChange={set("date_of_birth")} autoComplete="bday" className={field} data-testid="intake-dob" />
                </div>
                <div>
                  <label className={labelCls}>{c.email} *</label>
                  <input type="email" inputMode="email" value={f.email} onChange={set("email")} autoComplete="email" className={field} data-testid="intake-email" />
                </div>
                <div>
                  <label className={labelCls}>{c.phone} *</label>
                  <input type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" className={field} data-testid="intake-phone" />
                </div>
              </div>
            )}

            {/* STEP 2 — YOUR PRACTICE */}
            {step === 2 && (
              <div className="space-y-12">
                <div>
                  <p className="font-sans text-base text-onyx/80">{c.experienceQ} *</p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {c.experienceOptions.map(([v, l]) => (
                      <button
                        type="button"
                        key={v}
                        onClick={() => setV("experience")(v)}
                        className={`border px-5 py-4 text-left font-sans text-base transition-colors duration-300 ${f.experience === v ? "border-onyx bg-onyx text-petal" : "border-onyx/25 text-onyx/70 hover:border-onyx hover:text-onyx"}`}
                        data-testid={`intake-experience-${v}`}
                        aria-pressed={f.experience === v}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-sans text-base text-onyx/80">{c.focusQ}</p>
                  <p className="mt-1 font-sans text-sm text-onyx/50">{c.focusHint}</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    {FOCUS_KEYS.map((k) => (
                      <button
                        type="button"
                        key={k}
                        onClick={() => toggleFocus(k)}
                        className={`label border px-5 py-3 transition-colors duration-300 ${f.focus_areas.includes(k) ? "border-onyx bg-onyx text-petal" : "border-onyx/25 text-onyx/60 hover:border-onyx hover:text-onyx"}`}
                        data-testid={`intake-focus-${k}`}
                        aria-pressed={f.focus_areas.includes(k)}
                      >
                        {c.focusOptions[k]}
                      </button>
                    ))}
                  </div>
                  {f.focus_areas.includes("other") && (
                    <div className="mt-6" data-testid="intake-focus-other-wrap">
                      <label className={labelCls}>{c.focusOtherLabel}</label>
                      <input value={f.focus_other} onChange={set("focus_other")} className={field} data-testid="intake-focus-other" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-sans text-base text-onyx/80">{c.goalsQ}</p>
                  <p className="mt-1 font-sans text-sm text-onyx/50">{c.optional}</p>
                  <textarea rows={3} value={f.goals_notes} onChange={set("goals_notes")} className={`${field} resize-none`} data-testid="intake-goals" />
                </div>
              </div>
            )}

            {/* STEP 3 — GOOD TO KNOW */}
            {step === 3 && (
              <div className="space-y-12">
                <div className="border-b border-onyx/10 pb-10">
                  <p className="font-sans text-base text-onyx/80">{c.complaintsQ}</p>
                  <YesNo value={f.complaints} onChange={setV("complaints")} options={[["no", c.no], ["yes", c.yes]]} testid="intake-complaints" />
                  {f.complaints === "yes" && (
                    <div className="mt-6" data-testid="intake-complaints-detail-wrap">
                      <label className={labelCls}>{c.tellMore} *</label>
                      <textarea rows={3} value={f.complaints_detail} onChange={set("complaints_detail")} className={`${field} resize-none`} data-testid="intake-complaints-detail" />
                    </div>
                  )}
                </div>
                <div className="border-b border-onyx/10 pb-10">
                  <p className="font-sans text-base text-onyx/80">{c.avoidQ}</p>
                  <YesNo value={f.avoids} onChange={setV("avoids")} options={[["no", c.no], ["yes", c.yes]]} testid="intake-avoids" />
                  {f.avoids === "yes" && (
                    <div className="mt-6" data-testid="intake-avoid-detail-wrap">
                      <label className={labelCls}>{c.tellMore} *</label>
                      <textarea rows={3} value={f.avoid_detail} onChange={set("avoid_detail")} className={`${field} resize-none`} data-testid="intake-avoid-detail" />
                    </div>
                  )}
                </div>
                <div className="border-b border-onyx/10 pb-10">
                  <p className="font-sans text-base text-onyx/80">{c.treatmentQ}</p>
                  <YesNo value={f.treatment} onChange={setV("treatment")} options={[["no", c.no], ["yes", c.yes]]} testid="intake-treatment" />
                  {f.treatment === "yes" && (
                    <div className="mt-6" data-testid="intake-treatment-detail-wrap">
                      <label className={labelCls}>{c.shareRelevant}</label>
                      <textarea rows={3} value={f.treatment_detail} onChange={set("treatment_detail")} className={`${field} resize-none`} data-testid="intake-treatment-detail" />
                    </div>
                  )}
                </div>
                <div className="border-b border-onyx/10 pb-10">
                  <p className="font-sans text-base text-onyx/80">{c.pregnancyQ}</p>
                  <YesNo value={f.pregnancy} onChange={setV("pregnancy")} options={[["no", c.no], ["yes", c.yes], ["prefer_not_to_say", c.preferNot]]} testid="intake-pregnancy" />
                  {f.pregnancy === "yes" && (
                    <div className="mt-6" data-testid="intake-pregnancy-detail-wrap">
                      <label className={labelCls}>{c.pregnancyDetail}</label>
                      <textarea rows={2} value={f.pregnancy_detail} onChange={set("pregnancy_detail")} className={`${field} resize-none`} data-testid="intake-pregnancy-detail" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-sans text-base text-onyx/80">{c.otherQ}</p>
                  <p className="mt-1 font-sans text-sm text-onyx/50">{c.optional}</p>
                  <textarea rows={3} value={f.other_notes} onChange={set("other_notes")} className={`${field} resize-none`} data-testid="intake-other" />
                </div>
              </div>
            )}

            {/* STEP 4 — CONFIRMATION */}
            {step === 4 && (
              <div className="space-y-12">
                <div className="border border-onyx/20 p-6 md:p-8" data-testid="intake-important">
                  <p className="label text-terracotta">{c.importantLabel}</p>
                  <p className="mt-4 font-sans text-sm leading-relaxed text-onyx/75">{c.importantBody}</p>
                </div>
                <div className="space-y-6">
                  {c.consents.map((txt, i) => (
                    <label key={i} className="flex cursor-pointer items-start gap-4 border-b border-onyx/10 pb-6" data-testid={`intake-consent-${i + 1}-label`}>
                      <input
                        type="checkbox"
                        checked={f[`c${i + 1}`]}
                        onChange={(e) => setF((p) => ({ ...p, [`c${i + 1}`]: e.target.checked }))}
                        className="mt-1 h-5 w-5 shrink-0 accent-onyx"
                        data-testid={`intake-consent-${i + 1}`}
                      />
                      <span className="font-sans text-sm leading-relaxed text-onyx/75">{txt}</span>
                    </label>
                  ))}
                  <label className="flex cursor-pointer items-start gap-4 border-b border-onyx/10 pb-6" data-testid="intake-consent-5-label">
                    <input
                      type="checkbox"
                      checked={f.c5}
                      onChange={(e) => setF((p) => ({ ...p, c5: e.target.checked }))}
                      className="mt-1 h-5 w-5 shrink-0 accent-onyx"
                      data-testid="intake-consent-5"
                    />
                    <span className="font-sans text-sm leading-relaxed text-onyx/75">
                      {c.termsPre}
                      <Link to="/terms" target="_blank" className="underline underline-offset-2 hover:opacity-60" data-testid="intake-terms-link">{c.termsLink}</Link>
                      {c.termsPost}
                    </span>
                  </label>
                </div>
                <div className="grid gap-10 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>{c.signatureLabel} *</label>
                    <input value={f.digital_signature} onChange={set("digital_signature")} autoComplete="name" className={field} data-testid="intake-signature" />
                  </div>
                  <div>
                    <label className={labelCls}>{c.dateLabel}</label>
                    <p className="border-b border-onyx/25 py-4 font-sans text-lg text-onyx/70" data-testid="intake-date">{today}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* NAVIGATION */}
          <div className="mt-14 flex items-center justify-between gap-4">
            {step > 1 ? (
              <button type="button" onClick={() => goTo(step - 1)} className="label border-b border-onyx/40 pb-1 text-onyx/60 transition-colors hover:text-onyx" data-testid="intake-back">
                ← {c.back}
              </button>
            ) : <span />}
            {step < 4 ? (
              <button type="button" onClick={next} className="label border border-onyx bg-onyx px-10 py-5 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx" data-testid="intake-next">
                {c.next} →
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={status === "sending"} className="label border border-onyx bg-onyx px-10 py-5 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx disabled:opacity-50" data-testid="intake-submit">
                {status === "sending" ? c.sending : c.submit}
              </button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
